import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { CategoryKey } from '../types'

// Tailwind class merge utility
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Score to color class
export function scoreColor(score: number, max: number = 100): string {
  const pct = (score / max) * 100
  if (pct >= 70) return 'score-good'
  if (pct >= 50) return 'score-fair'
  return 'score-poor'
}

// Score to stroke color for SVG rings
export function scoreStroke(score: number, max: number = 100): string {
  const pct = (score / max) * 100
  if (pct >= 70) return '#16a34a'
  if (pct >= 50) return '#d97706'
  return '#dc2626'
}

// Grade to color class
export function gradeColor(grade: string): string {
  switch (grade) {
    case 'A': return 'grade-A'
    case 'B': return 'grade-B'
    case 'C': return 'grade-C'
    case 'D': return 'grade-D'
    default: return 'grade-F'
  }
}

// Grade to i18n translation key (scoring namespace)
// Usage: t(gradeLabel(score)) — where t comes from useTranslation('scoring')
export function gradeLabel(score: number): string {
  if (score >= 80) return 'grade.good'
  if (score >= 60) return 'grade.fair'
  if (score >= 40) return 'grade.needsWork'
  return 'grade.poor'
}

// Localised relative time — requires a t() function from useTranslation('common')
export function localizedTimeAgo(
  dateStr: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return t('time.today')
  if (diffDays === 1) return t('time.yesterday')
  if (diffDays < 7) return t('time.daysAgo', { count: diffDays })
  if (diffDays < 30) return t('time.weeksAgo', { count: Math.floor(diffDays / 7) })
  if (diffDays < 365) return t('time.monthsAgo', { count: Math.floor(diffDays / 30) })
  return t('time.yearsAgo', { count: Math.floor(diffDays / 365) })
}

// Format number with commas
export function formatNumber(n: number): string {
  return n.toLocaleString()
}

// Format distance in meters to readable string
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`
  return `${(meters / 1000).toFixed(1)}km`
}

// Format relative time
export function timeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}y ago`
}

// Category color accent
export function categoryAccent(key: CategoryKey): string {
  switch (key) {
    case 'search_results':   return '#16a34a'
    case 'guest_experience': return '#d97706'
    case 'local_listings':   return '#2563eb'
    default: return '#6b7280'
  }
}

// Severity to color
export function severityColor(severity: string): string {
  switch (severity) {
    case 'critical': return '#dc2626'
    case 'warning':  return '#d97706'
    default:         return '#16a34a'
  }
}

// Impact/effort to badge color
export function impactColor(level: string): string {
  switch (level) {
    case 'high':   return 'bg-red-100 text-red-700'
    case 'medium': return 'bg-yellow-100 text-yellow-700'
    default:       return 'bg-green-100 text-green-700'
  }
}

export function effortColor(level: string): string {
  switch (level) {
    case 'high':   return 'bg-gray-100 text-gray-700'
    case 'medium': return 'bg-blue-50 text-blue-700'
    default:       return 'bg-green-50 text-green-700'
  }
}

// SVG ring offset calculation
// circumference for r=45 is ~282.7
export function ringOffset(score: number, max: number = 100, circumference = 283): number {
  const pct = Math.min(Math.max(score / max, 0), 1)
  return circumference * (1 - pct)
}

// Truncate text
export function truncate(str: string, n: number): string {
  return str.length > n ? str.slice(0, n) + '...' : str
}

// Star rating display
export function starRating(rating: number): string {
  const full = Math.floor(rating)
  const half = rating % 1 >= 0.5 ? 1 : 0
  const empty = 5 - full - half
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(empty)
}
